/**
 * The Events tab: the three master switches, the search, and the grid as six
 * sections of topics. Sections and topics all start collapsed.
 *
 * Retired rows sit behind "Show retired". Its count follows the search, so it is
 * how a retired event stays findable by name.
 *
 * @link ./useEventsView for the search, open and retired state.
 */
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { BellOff, Search } from 'lucide-react';
import { NotificationDomainSection } from './NotificationDomainSection';
import { NotificationMasterCard } from './NotificationMasterCard';
import type { EventSideFilter } from './eventReach';
import type { OneDailyDelivery } from './types';
import type { EventsView } from './useEventsView';
import type { UseNotificationSetupResult } from './useNotificationSetup';

const SIDE_CHOICES: { value: EventSideFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
];

interface NotificationEventsTabProps {
  setup: UseNotificationSetupResult;
  view: EventsView;
  /** CR-8217: refused to anyone but the customer side. */
  canSetInstallSwitches: boolean;
  /** Batched mail is really going out: Digest on, and email not stopped. */
  batchedMail: boolean;
  /** What the one daily digest does for this tenant right now. */
  digestDelivery: OneDailyDelivery;
}

export function NotificationEventsTab({
  setup,
  view,
  canSetInstallSwitches,
  batchedMail,
  digestDelivery,
}: NotificationEventsTabProps) {
  const hasEvents = (setup.catalogue?.eventCount ?? 0) > 0;
  const anyVisible = view.sections.some((section) => section.visible.length > 0);

  return (
    <>
      <NotificationMasterCard
        master={setup.master}
        canConfigure={setup.canConfigure}
        canSetInstallSwitches={canSetInstallSwitches}
        oneDailyDigest={setup.catalogue?.oneDailyDigest ?? false}
        saving={setup.savingSettings}
        onChange={(patch, message) => void setup.saveSettings(patch, message)}
        onEmailsEnabledChange={(enabled) => void setup.setEmailsEnabled(enabled)}
      />

      {!hasEvents ? (
        <Card>
          <CardContent className="space-y-2 py-12 text-center">
            <BellOff className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="font-medium">No notification events available</p>
            <p className="text-sm text-muted-foreground">
              Nothing has been shipped to the event catalogue for this platform yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                aria-label="Search events"
                placeholder="Search events…"
                value={view.search}
                onChange={(e) => view.setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1 rounded-lg border p-1" role="group" aria-label="Who it reaches">
              {SIDE_CHOICES.map(({ value, label }) => (
                <Button
                  key={value}
                  type="button"
                  variant={view.side === value ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 px-2"
                  aria-pressed={view.side === value}
                  onClick={() => view.setSide(value)}
                >
                  {value === 'all' ? label : `${label} (${view.sideCounts[value]})`}
                </Button>
              ))}
            </div>
          </div>

          {!anyVisible ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {view.search.trim()
                  ? `No notification events match “${view.search}”${view.side === 'all' ? '' : ` on the ${view.side} side. Try All`}.`
                  : 'No notification events reach this side.'}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {view.sections.map((section) => (
                <NotificationDomainSection
                  key={section.domain}
                  label={section.label}
                  // The filtered set, and the only set this section's bulk
                  // buttons may touch — a search must not reach past itself.
                  events={section.visible}
                  allEvents={section.events}
                  showRetired={view.showRetired}
                  open={view.isOpen(section.domain)}
                  onOpenChange={(open) => view.setOpen(section.domain, open)}
                  isTopicOpen={view.isTopicOpen}
                  onTopicOpenChange={view.setTopicOpen}
                  // A Save ends in a reload, which clears anything staged since.
                  canConfigure={setup.canConfigure && !setup.saving}
                  frequencyChoices={setup.catalogue?.frequencyChoices ?? []}
                  defaultFrequency={setup.master.defaultFrequency}
                  emailsEnabled={setup.master.emailsEnabled}
                  digestEnabled={batchedMail}
                  digestDelivery={digestDelivery}
                  configFor={setup.configFor}
                  isDirty={setup.isDirty}
                  onUpdate={setup.updateEvent}
                  onToggleChannel={setup.toggleChannel}
                  onReset={setup.resetEvent}
                  onSetEventsEnabled={setup.setEventsEnabled}
                  onSetTopicChannel={setup.setTopicChannel}
                />
              ))}
              {view.retiredCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-pressed={view.showRetired}
                  onClick={view.toggleRetired}
                >
                  {view.showRetired ? 'Hide' : 'Show'} retired ({view.retiredCount})
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
